/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable no-alert */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import ReactTimeAgo from 'react-time-ago'
import { toast } from 'react-toastify'
import { ReactComponent as CheckIcon } from '@material-design-icons/svg/round/check.svg'
import { useMutation, useQueryClient } from '@tanstack/react-query'
// import { unlike } from 'api'

import {
  deleteCancelReportPost,
  deletePost,
  followRequest,
  likeRequest,
  postReportPost,
  unFollowRequest,
  unlikeRequest,
} from 'api'
import { useSwiper } from 'swiper/react'

import IconButton from 'components/common/IconButton'
import ArrowDown from 'components/icons/ArrowDown'
import ArrowUp from 'components/icons/ArrowUp'
import CopyIcon from 'components/icons/CopyIcon'
import LikeIcon from 'components/icons/LikeIcon'
import PlayIcon from 'components/icons/PlayIcon'
import UnLikeIcon from 'components/icons/UnLikeIcon'
import ViewsIcon from 'components/icons/ViewsIcon'
import ConfirmModal from 'components/modals/ConfirmModal'
import { SettingConfirmModalProps } from 'components/SettingsPost/SettingsPost'
import VerifiedIcon from 'components/VerifiedIcon'
import VolumeSetting from 'components/VolumeSetting'
import { setLogInModalOpen } from 'store/reducers/modalReducer'
import useFetchRequestMyProfile from 'hooks/query/useFetchRequestMyProfile'
import { useAppSelector } from 'hooks/store'
import useCopy from 'hooks/useCopy'
import useModal from 'hooks/useModal'
import useResize from 'hooks/useResize'
import { PostFeed } from 'types/Post'
import SettingsPost from '../SettingsPost'

import c from './TrendingVideoPost.module.scss'

interface PostProps {
  post: PostFeed
  imgClassName?: string
  withLink?: boolean
  trending?: boolean
  feed?: boolean
  setMuted: () => void
  slideIndex: number
  volume: number
  activeIndex: number
}

function TrendingVideoPost({
  post,
  imgClassName = '',
  withLink = false,
  trending = false,
  feed = false,
  slideIndex,
  volume,
  setMuted,
  activeIndex,
}: PostProps) {
  const { t } = useTranslation()
  const language = useAppSelector((state) => state.settings.language)
  const [isLiked, setIsLiked] = useState<boolean>(post.is_liked)
  const [likesCount, setLikesCount] = useState<number>(post.likes_count)
  const { isCopied, handleCopy } = useCopy(post.assets?.[0]?.source)
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [showButton, setShowButton] = useState<boolean>(false)
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const textRef = useRef<HTMLDivElement>(null)
  const [isConfirmModal, setIsConfirmModal] =
    useState<SettingConfirmModalProps>({
      isOpen: false,
      isDelete: false,
      isReport: false,
    })

  const width = useResize()

  const getWidthContainer = () => {
    const containerEl = containerRef.current

    if (containerEl) {
      const { height } = containerEl.getBoundingClientRect()
      return height + 10
    }

    return 130
  }

  const [height, setHeight] = useState<number>(0)

  const [isPlaying, setIsPlaying] = useState<boolean>(
    slideIndex === activeIndex,
  )

  const handleVolumeChange = (newVolume: number) => {
    if (videoRef.current) {
      videoRef.current.volume = newVolume
    }
  }

  const truncateText = (text: string) => {
    return text.length > 100 ? `${text.substring(0, 100)}... ` : text
  }

  useEffect(() => {
    handleVolumeChange(volume)
  }, [volume])

  useEffect(() => {
    const containerEl = containerRef.current
    if (containerEl) {
      setHeight(getWidthContainer())
    }
  }, [isExpanded])

  const swiper = useSwiper()

  useEffect(() => {
    if (videoRef?.current) {
      const videoEl = videoRef.current
      if (slideIndex === activeIndex) {
        videoEl.play()
        setIsPlaying(false)
      } else {
        videoEl.pause()
      }
    }
  }, [activeIndex])
  const { data: user } = useFetchRequestMyProfile()

  const queryClient = useQueryClient()

  const [isFollowed, setIsFollowed] = useState(post.user?.is_subscribed)
  const mutationLike = useMutation<void, Error>(() => {
    return likeRequest(post.id!)
  })
  const mutationUnLike = useMutation<void, Error>(() => {
    return unlikeRequest(post.id!)
  })

  const mutationDelete = useMutation<void, Error>(() => {
    return deletePost(post.id!)
  })
  const mutationReport = useMutation<void, Error>(() => postReportPost(post.id))
  const mutationCancelReport = useMutation<void, Error>(() =>
    deleteCancelReportPost(post.id),
  )

  const { logInModalOpen } = useAppSelector((state) => state.modal)
  const { open: openLogInModal } = useModal(logInModalOpen, setLogInModalOpen)

  const copyIcon = useMemo(() => {
    return isCopied ? (
      <CheckIcon className={c.icon} style={{ fill: 'currentColor' }} />
    ) : (
      <CopyIcon />
    )
  }, [isCopied])

  const mutationFollow = useMutation<void, Error>(() => {
    return followRequest(post.user.id)
  })
  const mutationUnFollow = useMutation<void, Error>(() => {
    return unFollowRequest(post.user.id!)
  })

  const toggleText = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  const likeHandler = () => {
    if (user?.id !== post.user.id) {
      if (isLiked) {
        setLikesCount((prev) => prev - 1)
        setIsLiked(false)
        mutationUnLike.mutate()
      } else {
        setLikesCount((prev) => prev + 1)
        setIsLiked(true)
        mutationLike.mutate()
      }
    } else {
      toast.error("You can't like your own post")
    }
  }

  const deleteYourPost = useCallback(() => {
    try {
      mutationDelete.mutate()
      toast.success(t('general.post.successDelete'))
    } catch (error) {
      console.log(error)
      toast.success(t('error.somethingWrong'))
    }
  }, [mutationDelete])

  const reportPost = useCallback(() => {
    try {
      if (post.is_strike) {
        mutationCancelReport.mutateAsync().then(() => {
          queryClient.invalidateQueries({
            queryKey: [`infinite-requests-trending`],
          })
          queryClient.refetchQueries({
            queryKey: [`infinite-requests-trending`],
          })
          toast.success(t('general.post.successCancelReport'))
        })
      } else {
        mutationReport.mutateAsync().then(() => {
          queryClient.invalidateQueries({
            queryKey: [`infinite-requests-trending`],
          })
          queryClient.refetchQueries({
            queryKey: [`infinite-requests-trending`],
          })
          toast.success(t('general.post.successReport'))
        })
      }
    } catch (error) {
      console.log(error)
      toast.success(t('error.somethingWrong'))
    }
  }, [mutationReport])

  const { data: myProfileData } = useFetchRequestMyProfile()

  const isMyProfile = useMemo(
    () => post.user.nickname === myProfileData?.nickname,
    [post.user.nickname, myProfileData?.nickname],
  )

  const followHandler = () => {
    if (isFollowed) {
      setIsFollowed(false)
      mutationUnFollow.mutate()
    } else {
      setIsFollowed(true)
      mutationFollow.mutate()
    }
  }

  const toggleVideo = useCallback(
    (e: React.MouseEvent<HTMLVideoElement>) => {
      setIsPlaying((prev) => !prev)
      if (isPlaying) {
        e.currentTarget.play()
      } else {
        e.currentTarget.pause()
      }
    },
    [isPlaying],
  )

  const toggleVideoStart = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const videoEl = e.currentTarget.parentElement?.querySelector('video')
      if (videoEl) {
        if (isPlaying) {
          videoEl.play()
        } else {
          videoEl.pause()
        }
      }
    },
    [isPlaying],
  )

  useEffect(() => {
    if (textRef.current) {
      const isOverflowing =
        textRef.current.scrollHeight > textRef.current.clientHeight
      setShowButton(isOverflowing)
    }
  }, [post])

  const cancelConfirm = useCallback(() => {
    setIsConfirmModal({
      isDelete: false,
      isOpen: false,
      isReport: false,
    })
  }, [])

  const handleConfirm = useCallback(() => {
    if (isConfirmModal.isDelete) {
      deleteYourPost()
    }
    if (isConfirmModal.isReport) {
      reportPost()
    }
    cancelConfirm()
  }, [isConfirmModal, deleteYourPost])

  const confirmModalHeading = useMemo(() => {
    if (isConfirmModal.isDelete) {
      return t('general.post.deleteTextModal')
    }
    if (isConfirmModal.isReport) {
      return t('general.post.reportTextModal')
    }

    return ''
  }, [isConfirmModal])

  return (
    <div className={c.post}>
      <ConfirmModal
        isModalOpen={isConfirmModal.isOpen}
        confirmFunc={handleConfirm}
        cancelFunc={cancelConfirm}
        heading={confirmModalHeading}
      />
      <div className={c.firstColumn}>
        <button
          type="button"
          onClick={() => swiper.slidePrev()}
          className={c.navButtonPrev}
        >
          <ArrowDown />
        </button>
        <button
          type="button"
          onClick={() => swiper.slideNext()}
          className={c.navButtonNext}
        >
          <ArrowUp />
        </button>
      </div>
      <div className={c.secondColumn}>
        <div
          className={`${
            trending || post.assets?.[0].source_type === 'video'
              ? c.postImgWrapperTrending
              : c.postImgWrapper
          } ${imgClassName}`}
        >
          {post.assets?.[0].source_type === 'video' && post.assets?.[0].url && (
            <>
              <div className={c.videoContainer}>
                {isPlaying && (
                  <button
                    type="button"
                    className={c.statusVideo}
                    onClick={toggleVideoStart}
                  >
                    <PlayIcon className={c.playButton} />
                  </button>
                )}
                <video
                  ref={videoRef}
                  src={post.assets?.[0].url}
                  className={c.postImg}
                  onClick={toggleVideo}
                  loop
                />
              </div>
              <div className={c.postBottom} ref={containerRef}>
                <div className={c.postWrapper}>
                  <div className={c.postUser}>
                    <div className={c.avatarWrapper}>
                      {post.user.avatar && (
                        <img
                          className={c.avatar}
                          src={post.user.avatar}
                          alt=""
                        />
                      )}
                    </div>
                    <div className={c.nicknameWrapper}>
                      <span className={c.userInfo}>
                        <NavLink
                          className={c.link}
                          to={`/profile/${post.user.nickname}`}
                          end
                        >
                          <p className={c.nickname}>
                            {post.user.nickname}{' '}
                            {post.user.is_paid_subscription && <VerifiedIcon />}
                          </p>
                          {post.created_at && (
                            <p className={c.date}>
                              <ReactTimeAgo
                                date={new Date(post.created_at)}
                                locale={language === 'en' ? 'en-US' : 'ru'}
                              />
                            </p>
                          )}
                        </NavLink>
                      </span>
                      <div className={c.followButton}>
                        {!isMyProfile && (
                          <button
                            type="button"
                            className={`${c.follow} ${
                              isFollowed ? c.followed : ''
                            } ${c.followMobile}`}
                            onClick={followHandler}
                          >
                            {mutationFollow.isLoading ? (
                              t('general.loading')
                            ) : (
                              <>
                                {isFollowed && t('profile.unFollow')}
                                {!isFollowed && t('profile.follow')}
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={c.descriptionContainer}>
                    <div className={c.text}>
                      <span>
                        {isExpanded
                          ? post.assets?.[0]?.source
                          : truncateText(post.assets?.[0]?.source)}
                      </span>
                      {post.assets?.[0]?.source.length > 100 && (
                        <button
                          type="button"
                          className={c.buttonTextContainer}
                          onClick={toggleText}
                        >
                          {isExpanded ? (
                            <span className={c.buttonText}>
                              {t('general.post.hideText')}
                            </span>
                          ) : (
                            <span className={c.buttonText}>
                              {t('general.post.showText')}
                            </span>
                          )}
                        </button>
                      )}
                    </div>

                    <IconButton
                      className={c.postCopy}
                      disabled={isCopied}
                      onClick={handleCopy}
                    >
                      {copyIcon}
                    </IconButton>
                  </div>
                  <div className={c.postTags}>
                    {post.assets?.[0]?.style &&
                      post.assets?.[0]?.style !== 'undefined' && (
                        <div className={c.textStyleContainer}>
                          <span className={c.textStyle}>
                            {post.assets?.[0]?.style}
                          </span>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <div
        className={c.thirdColumn}
        style={
          width < 640
            ? {
                bottom: `${height}px`,
              }
            : {}
        }
      >
        <div className={c.postFeedBack}>
          <div
            className={c.postLikes}
            onClick={!user?.id ? openLogInModal : likeHandler}
            onKeyDown={!user?.id ? openLogInModal : likeHandler}
            role="button"
            tabIndex={0}
          >
            <div className={c.postLikesIcon}>
              {isLiked ? (
                <LikeIcon className={c.iconSetting} />
              ) : (
                <UnLikeIcon className={c.iconSetting} />
              )}
            </div>
            <p className={c.postLikesCount}>{likesCount}</p>
          </div>
          <div className={c.volumeSliderContainer}>
            <VolumeSetting setMuted={setMuted} volume={volume} />
          </div>
          {(user?.role === 'admin' || post.user.id === user?.id) && (
            <div className={c.settingsPost}>
              <SettingsPost
                userData={user}
                postData={post}
                setIsConfirmModal={setIsConfirmModal}
              />
            </div>
          )}
          <div className={c.postViews}>
            <ViewsIcon />
            {`${post.views_count} `}
          </div>
        </div>
      </div>
    </div>
  )
}

export default TrendingVideoPost
